import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { API_CONFIG, getTenantDomain } from '../utils/constants';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const DEFAULT_PRIMARY_COLOR = '#4cb7ec';
const DEFAULT_LOGO_URL = '/hqms_logo.png';

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '13, 110, 253';
};

const applyPrimaryColor = (color) => {
  const root = document.documentElement;
  root.style.setProperty('--primary-color', color);
  root.style.setProperty('--primary-color-rgb', hexToRgb(color));
  root.style.setProperty('--sidebar-active-bg', color);
  root.style.setProperty('--btn-primary-bg', color);
};

// Resolve a logo path to a full URL
const resolveLogoUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:') || url.startsWith('blob:')) return url;
  return `${API_CONFIG.BASE_URL}/${url}`;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'light';
  });

  // All branding values start from defaults; fetched fresh from API on every mount
  const [primaryColor, setPrimaryColorState] = useState(DEFAULT_PRIMARY_COLOR);
  const [logoUrl, setLogoUrlState] = useState(DEFAULT_LOGO_URL);
  const [faviconUrl, setFaviconUrlState] = useState('');
  const [logoAnimation, setLogoAnimationState] = useState('none');
  const [hospitalName, setHospitalNameState] = useState('');
  const [ambulanceNumber, setAmbulanceNumberState] = useState('');
  const [bloodBankNumber, setBloodBankNumberState] = useState('');
  const [medicalNumber, setMedicalNumberState] = useState('');
  const [pathologyNumber, setPathologyNumberState] = useState('');
  const [xrayNumber, setXrayNumberState] = useState('');

  // Apply theme to document root
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Apply primary color to CSS variables
  useEffect(() => {
    applyPrimaryColor(primaryColor);
  }, [primaryColor]);

  // Dynamically update document favicon
  useEffect(() => {
    const href = faviconUrl || '/hqms_favicon.png';
    const iconLink = document.querySelector("link[rel='icon']");
    const appleLink = document.querySelector("link[rel='apple-touch-icon']");
    if (iconLink) iconLink.href = href;
    if (appleLink) appleLink.href = href;
  }, [faviconUrl]);

  // Fetch branding from API on mount
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const tenantDomain = getTenantDomain();
        const params = new URLSearchParams();
        if (tenantDomain) {
          params.set('domain', tenantDomain);
        }
        const queryString = params.toString();
        const brandingUrl = `${API_CONFIG.BASE_URL}/api/branding${queryString ? '?' + queryString : ''}`;

        const response = await fetch(brandingUrl);
        const data = await response.json();

        if (!data.status || !data.data) return;
        const d = data.data;

        if (d.primary_color) setPrimaryColorState(d.primary_color);
        if (d.logo_url) setLogoUrlState(resolveLogoUrl(d.logo_url));
        if (d.favicon_url) setFaviconUrlState(resolveLogoUrl(d.favicon_url));
        if (d.logo_animation) setLogoAnimationState(d.logo_animation);

        setAmbulanceNumberState(d.ambulance_number || '');
        setBloodBankNumberState(d.blood_bank_number || '');
        setMedicalNumberState(d.medical_number || '');
        setPathologyNumberState(d.pathology_number || '');
        setXrayNumberState(d.xray_number || '');
        // hospital_name is set from logged-in user via HospitalNameSync, not here
      } catch (_) {
        // Silently fail — defaults remain in place
      }
    };
    fetchBranding();
  }, []);

  // Update document title whenever hospitalName changes
  useEffect(() => {
    document.title = hospitalName ? `Webdekho 🤝 ${hospitalName}` : 'Webdekho 🏥 HQMS';
  }, [hospitalName]);

  const toggleTheme = useCallback(() => {
    setTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
  }, []);

  const setPrimaryColor = useCallback((color) => {
    setPrimaryColorState(color);
  }, []);
  const setLogoUrl = useCallback((url) => {
    setLogoUrlState(resolveLogoUrl(url) || DEFAULT_LOGO_URL);
  }, []);
  const setFaviconUrl = useCallback((url) => {
    setFaviconUrlState(resolveLogoUrl(url));
  }, []);
  const setLogoAnimation = useCallback((anim) => {
    setLogoAnimationState(anim || 'none');
  }, []);
  const setHospitalName = useCallback((name) => {
    setHospitalNameState(name || '');
  }, []);
  const setAmbulanceNumber = useCallback((num) => {
    setAmbulanceNumberState(num || '');
  }, []);
  const setBloodBankNumber = useCallback((num) => {
    setBloodBankNumberState(num || '');
  }, []);
  const setMedicalNumber = useCallback((num) => {
    setMedicalNumberState(num || '');
  }, []);
  const setPathologyNumber = useCallback((num) => {
    setPathologyNumberState(num || '');
  }, []);
  const setXrayNumber = useCallback((num) => {
    setXrayNumberState(num || '');
  }, []);

  const isDark = theme === 'dark';

  // Memoize context value to prevent cascading re-renders
  const value = useMemo(
    () => ({
      theme,
      toggleTheme,
      isDark,
      primaryColor,
      setPrimaryColor,
      logoUrl,
      setLogoUrl,
      faviconUrl,
      setFaviconUrl,
      logoAnimation,
      setLogoAnimation,
      hospitalName,
      setHospitalName,
      ambulanceNumber,
      setAmbulanceNumber,
      bloodBankNumber,
      setBloodBankNumber,
      medicalNumber,
      setMedicalNumber,
      pathologyNumber,
      setPathologyNumber,
      xrayNumber,
      setXrayNumber,
    }),
    [
      theme,
      isDark,
      primaryColor,
      logoUrl,
      faviconUrl,
      logoAnimation,
      hospitalName,
      ambulanceNumber,
      bloodBankNumber,
      medicalNumber,
      pathologyNumber,
      xrayNumber,
      toggleTheme,
      setPrimaryColor,
      setLogoUrl,
      setFaviconUrl,
      setLogoAnimation,
      setHospitalName,
      setAmbulanceNumber,
      setBloodBankNumber,
      setMedicalNumber,
      setPathologyNumber,
      setXrayNumber,
    ]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export default ThemeContext;
